const NAVY = '#1a1a2e';
const ACCENT = '#2563eb';

const VALUATION_BASIS_LABELS = {
  contract_rates: 'Contract Rates',
  new_rates: 'New Rates',
  fair_valuation: 'Fair Valuation',
  dayworks: 'Dayworks',
  mixed: 'Mixed',
};
function formatValuationBasis(value) {
  return VALUATION_BASIS_LABELS[value] || (value ? value.replace(/_/g, ' ') : '—');
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function formatCurrency(v) {
  if (v == null || v === '') return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function baseLayout(titleText, bodyHtml) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${titleText}</title>
</head>
<body style="margin:0;padding:0;background:#f3f4f6;font-family:'Segoe UI',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f3f4f6;padding:32px 0;">
  <tr><td align="center">
    <table width="640" cellpadding="0" cellspacing="0" style="max-width:640px;width:100%;">

      <!-- HEADER -->
      <tr>
        <td style="background:${NAVY};padding:28px 36px;border-radius:8px 8px 0 0;">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td>
                <div style="font-size:22px;font-weight:700;color:#ffffff;letter-spacing:0.04em;">
                  PROLINE INTERNALS LTD
                </div>
                <div style="font-size:12px;color:#a5b4fc;margin-top:4px;letter-spacing:0.08em;text-transform:uppercase;">
                  Commercial Management Portal
                </div>
              </td>
              <td align="right">
                <div style="font-size:11px;color:#94a3b8;text-align:right;line-height:1.5;">
                  Proline Internals Ltd<br/>
                  info@prolineinternals.co.uk
                </div>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- BODY -->
      <tr>
        <td style="background:#ffffff;padding:36px;border-left:1px solid #e5e7eb;border-right:1px solid #e5e7eb;">
          ${bodyHtml}
        </td>
      </tr>

      <!-- FOOTER -->
      <tr>
        <td style="background:#f8fafc;padding:20px 36px;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;">
          <p style="margin:0;font-size:11px;color:#9ca3af;line-height:1.6;">
            This email was sent automatically by the Proline Commercial Management System.
            Proline Internals Ltd · info@prolineinternals.co.uk<br/>
            <em>This communication and any attachments may be legally privileged and confidential.
            If you are not the intended recipient, please notify us immediately.</em>
          </p>
        </td>
      </tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;
}

function infoRow(label, value) {
  return `<tr>
    <td style="padding:7px 0;font-size:13px;font-weight:600;color:#6b7280;width:180px;vertical-align:top;">${label}</td>
    <td style="padding:7px 0;font-size:13px;color:#111111;vertical-align:top;">${value || '—'}</td>
  </tr>`;
}

function ctaButton(label, href) {
  return `<div style="text-align:center;margin:28px 0;">
    <a href="${href}" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:${ACCENT};color:#ffffff;font-size:15px;font-weight:600;
      padding:14px 36px;border-radius:6px;text-decoration:none;letter-spacing:0.02em;">
      ${label}
    </a>
  </div>`;
}

function jctBox(html) {
  return `<div style="background:#fffbeb;border-left:4px solid #f59e0b;padding:16px 20px;border-radius:0 6px 6px 0;margin:24px 0;">
    <div style="font-size:11px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;">
      Contractual Notice
    </div>
    <p style="margin:0;font-size:12px;color:#78350f;line-height:1.7;">${html}</p>
  </div>`;
}

// ─── Daywork Submission ──────────────────────────────────────────────────────

export function dayworkSubmissionEmail(daywork, project, instruction, mcQsName, portalLink) {
  const mcName = project?.main_contractor || 'Main Contractor';
  const subject = `Daywork Sheet ${daywork.reference} — For Approval — ${project?.name || ''}`;

  const body = `
    <h2 style="margin:0 0 4px;font-size:20px;color:${NAVY};">Daywork Sheet Submitted for Approval</h2>
    <p style="margin:0 0 24px;font-size:14px;color:#6b7280;">
      The following daywork sheet has been submitted by Proline Internals Ltd and requires your review and signature.
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${daywork.reference}</strong>`)}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Date of Works', formatDate(daywork.date_of_works))}
      ${infoRow('Instruction Ref', instruction?.reference || '—')}
      ${infoRow('Location', daywork.location || '—')}
      ${infoRow('Submitted', formatDateTime(daywork.submitted_at || new Date().toISOString()))}
    </table>

    ${jctBox(
      `This Daywork Sheet is submitted as a formal notice under the subcontract between Proline Internals Ltd and ${mcName}. ` +
      `The works described are considered to be outside the agreed subcontract scope. ` +
      `Proline Internals Ltd reserves all rights to recover the full value of this daywork under the subcontract conditions. ` +
      `Failure to respond within 21 days will be noted and may be referred to in any subsequent dispute resolution.`
    )}

    <p style="font-size:14px;color:#374151;margin:0 0 4px;">
      Please review the daywork sheet in the Proline portal and confirm your approval:
    </p>

    ${ctaButton('Review &amp; Approve Daywork Sheet', portalLink)}

    <p style="font-size:12px;color:#9ca3af;margin:0;">
      If the button above does not work, copy this link into your browser:<br/>
      <a href="${portalLink}" style="color:${ACCENT};word-break:break-all;">${portalLink}</a>
    </p>
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Variation Submission ────────────────────────────────────────────────────

export function variationSubmissionEmail(variation, project, instruction, mcQsName, portalLink) {
  const mcName = project?.main_contractor || 'Main Contractor';
  const subject = `Variation ${variation.reference} — For Review — ${project?.name || ''}`;

  const body = `
    <h2 style="margin:0 0 4px;font-size:20px;color:${NAVY};">Variation Quotation Submitted</h2>
    <p style="margin:0 0 24px;font-size:14px;color:#6b7280;">
      Proline Internals Ltd has submitted the following variation quotation for your review and response.
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:8px;">
      ${infoRow('Reference', `<strong>${variation.reference}</strong>`)}
      ${infoRow('Title', variation.title || '—')}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Linked Instruction', instruction?.reference || '—')}
      ${infoRow('Valuation Basis', formatValuationBasis(variation.valuation_basis))}
      ${infoRow('Submitted Value', `<strong style="color:#16a34a;">${formatCurrency(variation.value)}</strong>`)}
      ${infoRow('Revision', variation.current_revision > 0 ? `Rev ${variation.current_revision}` : 'Initial submission')}
    </table>

    ${variation.description ? `
    <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:6px;padding:14px 18px;margin:16px 0 24px;">
      <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px;">
        Scope of Works
      </div>
      <p style="margin:0;font-size:13px;color:#374151;line-height:1.6;">${variation.description}</p>
    </div>` : ''}

    ${jctBox(
      `This Variation Quotation is submitted as a formal notice under the subcontract between Proline Internals Ltd and ${mcName}. ` +
      `The works described are considered to be outside the agreed subcontract scope. ` +
      `Proline Internals Ltd reserves all rights to recover the full value of this variation under the subcontract conditions. ` +
      `Failure to respond within 21 days will be noted and may be referred to in any subsequent dispute resolution.`
    )}

    <p style="font-size:14px;color:#374151;margin:0 0 4px;">
      Please review the full variation quotation in the portal and record your response:
    </p>

    ${ctaButton('Review Variation Quotation', portalLink)}

    <p style="font-size:12px;color:#9ca3af;margin:0;">
      If the button above does not work, copy this link into your browser:<br/>
      <a href="${portalLink}" style="color:${ACCENT};word-break:break-all;">${portalLink}</a>
    </p>
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Daywork Agreement Confirmation ─────────────────────────────────────────

export function dayworkAgreementEmail(daywork, project, agreedBy, agreedAt) {
  const mcName = project?.main_contractor || 'Main Contractor';
  const agreedAtFmt = formatDateTime(agreedAt);
  const subject = `Daywork Sheet ${daywork.reference} — AGREED — ${project?.name || ''}`;

  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:#f0fdf4;border:2px solid #bbf7d0;border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:#16a34a;margin-bottom:12px;">✓</div>
      <h2 style="margin:0;font-size:20px;color:#16a34a;">Daywork Sheet Agreed</h2>
    </div>

    <p style="font-size:14px;color:#374151;text-align:center;margin:0 0 24px;">
      The following daywork sheet has been approved by ${mcName}.
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${daywork.reference}</strong>`)}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Date of Works', formatDate(daywork.date_of_works))}
      ${infoRow('Approved By', agreedBy || '—')}
      ${infoRow('Approved At', agreedAtFmt)}
    </table>

    ${jctBox(
      `This email confirms that ${agreedBy || 'the MC QS'} of ${mcName} approved this Daywork Sheet on ${agreedAtFmt}. ` +
      `This approval is binding under the subcontract conditions and the agreed value will be included in the next interim application for payment. ` +
      `This email serves as a formal record of agreement.`
    )}

    <p style="font-size:13px;color:#6b7280;text-align:center;margin:16px 0 0;">
      This confirmation has been recorded in the Proline commercial management system.
    </p>
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Variation Agreement Confirmation ───────────────────────────────────────

export function variationAgreementEmail(variation, project, agreedBy, agreedAt, agreedValue) {
  const mcName = project?.main_contractor || 'Main Contractor';
  const agreedAtFmt = formatDateTime(agreedAt);
  const subject = `Variation ${variation.reference} — AGREED — ${project?.name || ''}`;

  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:#f0fdf4;border:2px solid #bbf7d0;border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:#16a34a;margin-bottom:12px;">✓</div>
      <h2 style="margin:0;font-size:20px;color:#16a34a;">Variation Agreed</h2>
    </div>

    <p style="font-size:14px;color:#374151;text-align:center;margin:0 0 24px;">
      The following variation has been agreed by ${mcName}.
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:8px;">
      ${infoRow('Reference', `<strong>${variation.reference}</strong>`)}
      ${infoRow('Title', variation.title || '—')}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Agreed By', agreedBy || '—')}
      ${infoRow('Agreed At', agreedAtFmt)}
      ${infoRow('Submitted Value', formatCurrency(variation.value))}
      ${infoRow('Agreed Value', `<strong style="color:#16a34a;font-size:16px;">${formatCurrency(agreedValue ?? variation.value)}</strong>`)}
    </table>

    ${agreedValue != null && agreedValue !== variation.value ? `
    <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:12px 18px;margin:16px 0;">
      <p style="margin:0;font-size:12px;color:#92400e;">
        Note: The agreed value of ${formatCurrency(agreedValue)} differs from the submitted value of ${formatCurrency(variation.value)}.
        Please review the portal for the MC's comments.
      </p>
    </div>` : ''}

    ${jctBox(
      `This email confirms that ${agreedBy || 'the MC QS'} of ${mcName} agreed this Variation on ${agreedAtFmt}. ` +
      `The agreed value of ${formatCurrency(agreedValue ?? variation.value)} is binding under the subcontract conditions ` +
      `and will be included in the next interim application for payment. ` +
      `This email serves as a formal record of agreement.`
    )}

    <p style="font-size:13px;color:#6b7280;text-align:center;margin:16px 0 0;">
      This confirmation has been recorded in the Proline commercial management system.
    </p>
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Daywork Approval Notification to Proline ────────────────────────────────

export function dayworkApprovalNotification(daywork, project, approverName, approverCompany, approvedAt) {
  const subject = `APPROVED — Daywork ${daywork.reference} approved by ${approverCompany || approverName}`;
  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:#f0fdf4;border:2px solid #bbf7d0;border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:#16a34a;margin-bottom:12px;">✓</div>
      <h2 style="margin:0;font-size:20px;color:#16a34a;">Daywork Approved</h2>
    </div>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      <strong>${approverName}</strong>${approverCompany ? ` of <strong>${approverCompany}</strong>` : ''}
      has approved the following daywork sheet via the MC portal.
    </p>
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${daywork.reference}</strong>`)}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Date of Works', formatDate(daywork.date_of_works))}
      ${infoRow('Approved By', approverName)}
      ${infoRow('Company', approverCompany || '—')}
      ${infoRow('Approved At', formatDateTime(approvedAt))}
    </table>
    ${jctBox(`This approval is binding under the subcontract conditions. The value of this daywork should be included in the next interim application for payment.`)}
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Template 5: Daywork Challenge Notification to Proline ───────────────────

export function dayworkChallengeNotification(daywork, project, challengeReason, mcName, mcCompany) {
  const subject = `ACTION REQUIRED — Daywork ${daywork.reference} challenged by ${mcCompany || mcName}`;
  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:#fef2f2;border:2px solid #fecaca;border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:#dc2626;margin-bottom:12px;">!</div>
      <h2 style="margin:0;font-size:20px;color:#dc2626;">Daywork Challenged</h2>
    </div>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      <strong>${mcName}</strong>${mcCompany ? ` of <strong>${mcCompany}</strong>` : ''}
      has challenged the following daywork sheet. Review their reasons and respond.
    </p>
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${daywork.reference}</strong>`)}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Date of Works', formatDate(daywork.date_of_works))}
      ${infoRow('Challenged By', mcName)}
      ${infoRow('Company', mcCompany || '—')}
    </table>
    <div style="background:#fef2f2;border-left:4px solid #dc2626;padding:16px 20px;border-radius:0 6px 6px 0;margin:0 0 24px;">
      <div style="font-size:11px;font-weight:700;color:#dc2626;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;">Challenge Reason</div>
      <p style="margin:0;font-size:13px;color:#7f1d1d;line-height:1.7;">${challengeReason}</p>
    </div>
    <p style="font-size:14px;color:#374151;margin:0;">Log in to the Proline portal to review this daywork and prepare a response or revised submission.</p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Template 6: Variation Response Notification to Proline ─────────────────

export function variationResponseNotification(variation, project, action, mcName, mcCompany, reason, agreedValue) {
  const actionMap = {
    agreed: { label: 'AGREED', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: '✓' },
    part_agreed: { label: 'PART AGREED', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '~' },
    disputed: { label: 'DISPUTED', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: '!' },
  };
  const a = actionMap[action] || actionMap.disputed;
  const subject = `${a.label} — Variation ${variation.reference} — Response from ${mcCompany || mcName}`;
  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:${a.bg};border:2px solid ${a.border};border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:${a.color};margin-bottom:12px;">${a.icon}</div>
      <h2 style="margin:0;font-size:20px;color:${a.color};">Variation ${a.label}</h2>
    </div>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      <strong>${mcName}</strong>${mcCompany ? ` of <strong>${mcCompany}</strong>` : ''} has responded to your variation quotation.
    </p>
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${variation.reference}</strong>`)}
      ${infoRow('Title', variation.title || '—')}
      ${infoRow('Project', `${project?.reference || ''} — ${project?.name || ''}`)}
      ${infoRow('Response', `<strong style="color:${a.color};">${a.label}</strong>`)}
      ${infoRow('Submitted Value', formatCurrency(variation.value))}
      ${agreedValue != null ? infoRow('Agreed Value', `<strong style="color:${a.color};">${formatCurrency(agreedValue)}</strong>`) : ''}
      ${infoRow('Responded By', mcName)}
      ${infoRow('Company', mcCompany || '—')}
    </table>
    ${reason ? `<div style="background:${a.bg};border-left:4px solid ${a.border};padding:16px 20px;border-radius:0 6px 6px 0;margin:0 0 24px;"><div style="font-size:11px;font-weight:700;color:${a.color};text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;">${action === 'agreed' ? 'Notes' : 'Reason'}</div><p style="margin:0;font-size:13px;line-height:1.7;">${reason}</p></div>` : ''}
    <p style="font-size:14px;color:#374151;margin:0;">Log in to the Proline portal to review and ${action === 'disputed' ? 'prepare a revised submission' : 'record this in your payment application'}.</p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── EWN Submission ──────────────────────────────────────────────────────────

export function ewnSubmissionEmail({ ewn, recipientName, senderName, projectName, portalLink, attachmentFilenames }) {
  const subject = `${ewn.reference} — Early Warning Notice — ${projectName || ''}`;

  const impactLabels = {
    cost: 'Cost', programme: 'Programme / Time', quality: 'Quality',
    health_safety: 'Health & Safety', design: 'Design',
  };
  const impactList = (ewn.potential_impact || []).map(v => impactLabels[v] || v).filter(Boolean);

  const descPreview = ewn.description
    ? (ewn.description.length > 200 ? ewn.description.slice(0, 200) + '…see attached' : ewn.description)
    : '—';

  const pdfFileName = `${ewn.reference}.pdf`;
  const allAttachments = [
    `${pdfFileName} — formal notice document`,
    ...(attachmentFilenames || []).map(f => `${f} — supporting document`),
  ];

  const body = `
    <h2 style="margin:0 0 4px;font-size:20px;color:${NAVY};">Early Warning Notice</h2>
    <p style="margin:0 0 24px;font-size:14px;color:#6b7280;">
      Dear ${recipientName || 'Sir/Madam'},
    </p>
    <p style="margin:0 0 20px;font-size:14px;color:#374151;">
      Please find attached an Early Warning Notice raised by Proline Internals Ltd in accordance
      with the conditions of subcontract.
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${ewn.reference}</strong>`)}
      ${infoRow('Project', projectName || '—')}
      ${infoRow('Date Raised', formatDate(ewn.date))}
      ${ewn.location ? infoRow('Location', ewn.location) : ''}
      ${infoRow('Issue Description', descPreview)}
      ${impactList.length > 0 ? infoRow('Potential Impact', impactList.join(', ')) : ''}
      ${ewn.action_requested ? infoRow('Action Requested', ewn.action_requested) : ''}
    </table>

    ${jctBox(
      `This Early Warning Notice is issued pursuant to the subcontract conditions. ` +
      `Proline Internals Ltd hereby gives notice that the matter described above may give rise to ` +
      `an increase in the Subcontract Sum and/or an extension of time. ` +
      `The Main Contractor is requested to issue a formal written instruction in response to this notice. ` +
      `The full document and any attachments are included with this email.`
    )}

    <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:6px;padding:14px 18px;margin:24px 0;">
      <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px;">
        Attached to this email:
      </div>
      ${allAttachments.map(a => `<div style="font-size:13px;color:#374151;padding:2px 0;">• ${a}</div>`).join('')}
    </div>

    ${portalLink ? `
    <p style="font-size:14px;color:#374151;margin:0 0 8px;">
      To formally respond to this notice, please click the button below. You will be asked to confirm
      whether you will issue a formal written instruction, raise a query, or dispute the notice.
    </p>
    <div style="text-align:center;margin:20px 0;">
      <a href="${portalLink}" target="_blank" rel="noopener noreferrer"
        style="display:inline-block;background:#111827;color:#ffffff;font-size:15px;font-weight:600;
        padding:14px 36px;border-radius:6px;text-decoration:none;letter-spacing:0.02em;">
        Respond to this Notice
      </a>
    </div>
    <p style="font-size:12px;color:#9ca3af;margin:0 0 16px;">
      If the button above does not work, copy this link into your browser:<br/>
      <a href="${portalLink}" style="color:${ACCENT};word-break:break-all;">${portalLink}</a>
    </p>` : ''}

    <p style="font-size:14px;color:#374151;margin:0 0 4px;">
      If you have any questions please contact <strong>${senderName || 'Proline Internals Ltd'}</strong>.
    </p>
    <p style="font-size:12px;color:#9ca3af;margin:16px 0 0;">
      This notice has been recorded in the Proline Commercial Management System.
    </p>
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── CVI Submission ──────────────────────────────────────────────────────────

export function cviSubmissionEmail({ cvi, recipientName, senderName, projectName, portalLink, attachmentFilenames }) {
  const subject = `${cvi.reference} — Confirmation of Verbal Instruction — ${projectName || ''}`;

  const pdfFileName = `${cvi.reference}.pdf`;
  const allAttachments = [
    `${pdfFileName} — formal notice document`,
    ...(attachmentFilenames || []).map(f => `${f} — supporting document`),
  ];

  const body = `
    <h2 style="margin:0 0 4px;font-size:20px;color:${NAVY};">Confirmation of Verbal Instruction</h2>
    <p style="margin:0 0 24px;font-size:14px;color:#6b7280;">
      Dear ${recipientName || 'Sir/Madam'},
    </p>
    <p style="margin:0 0 20px;font-size:14px;color:#374151;">
      We write to confirm a verbal instruction received from your representative${cvi.verbal_date ? ` on ${formatDate(cvi.verbal_date)}` : ''}.
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${cvi.reference}</strong>`)}
      ${infoRow('Project', projectName || '—')}
      ${cvi.instructed_by_name ? infoRow('Verbal Instruction Given By', [cvi.instructed_by_name, cvi.instructed_by_role].filter(Boolean).join(', ')) : ''}
      ${cvi.verbal_date ? infoRow('Date of Verbal Instruction', formatDate(cvi.verbal_date) + (cvi.verbal_time ? ` at ${String(cvi.verbal_time).substring(0, 5)}` : '')) : ''}
      ${cvi.verbal_method ? infoRow('Method', cvi.verbal_method) : ''}
      ${infoRow('Works Instructed', cvi.description || '—')}
    </table>

    ${jctBox(
      `This Confirmation of Verbal Instruction is issued pursuant to the subcontract conditions. ` +
      `Proline Internals Ltd hereby confirms the verbal instruction described above and requests that the ` +
      `Main Contractor issues a formal written instruction confirming the same. ` +
      `Proline Internals Ltd will not proceed with the instructed works until a formal written instruction ` +
      `has been received unless otherwise agreed in writing.`
    )}

    <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:6px;padding:14px 18px;margin:24px 0;">
      <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px;">
        Attached to this email:
      </div>
      ${allAttachments.map(a => `<div style="font-size:13px;color:#374151;padding:2px 0;">• ${a}</div>`).join('')}
    </div>

    ${portalLink ? `
    <p style="font-size:14px;color:#374151;margin:0 0 8px;">
      To formally respond to this notice, please click the button below. You will be asked to confirm
      whether you will issue a formal written instruction, raise a query, or dispute the notice.
    </p>
    <div style="text-align:center;margin:20px 0;">
      <a href="${portalLink}" target="_blank" rel="noopener noreferrer"
        style="display:inline-block;background:#111827;color:#ffffff;font-size:15px;font-weight:600;
        padding:14px 36px;border-radius:6px;text-decoration:none;letter-spacing:0.02em;">
        Respond to this Notice
      </a>
    </div>
    <p style="font-size:12px;color:#9ca3af;margin:0 0 16px;">
      If the button above does not work, copy this link into your browser:<br/>
      <a href="${portalLink}" style="color:${ACCENT};word-break:break-all;">${portalLink}</a>
    </p>` : ''}

    <p style="font-size:14px;color:#374151;margin:0 0 4px;">
      If you have any questions please contact <strong>${senderName || 'Proline Internals Ltd'}</strong>.
    </p>
    <p style="font-size:12px;color:#9ca3af;margin:16px 0 0;">
      This notice has been recorded in the Proline Commercial Management System.
    </p>
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── EWN / CVI Response Notification to Proline ─────────────────────────────

export function ewnCviResponseNotification({ instruction, response, responseNotes, expectedDate, respondedBy, respondedCompany, respondedAt }) {
  const responseMap = {
    will_instruct: 'Will Issue Instruction',
    query: 'Query Raised',
    disputed: 'Disputed',
  };
  const responseLabel = responseMap[response] || response;
  const typeLabel = instruction.type === 'EWN' ? 'Early Warning Notice' : 'Confirmation of Verbal Instruction';
  const subject = `MC Response: ${instruction.reference} — ${responseLabel}`;

  const actionColor = response === 'will_instruct' ? '#16a34a' : response === 'query' ? '#d97706' : '#dc2626';
  const actionBg = response === 'will_instruct' ? '#f0fdf4' : response === 'query' ? '#fffbeb' : '#fef2f2';
  const actionBorder = response === 'will_instruct' ? '#bbf7d0' : response === 'query' ? '#fde68a' : '#fecaca';
  const actionIcon = response === 'will_instruct' ? '✓' : response === 'query' ? '?' : '!';

  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:${actionBg};border:2px solid ${actionBorder};border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:${actionColor};margin-bottom:12px;">${actionIcon}</div>
      <h2 style="margin:0;font-size:20px;color:${actionColor};">MC Response: ${responseLabel}</h2>
    </div>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      The Main Contractor has responded to <strong>${instruction.reference}</strong> (${typeLabel}).
    </p>
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${instruction.reference}</strong>`)}
      ${infoRow('Type', typeLabel)}
      ${infoRow('Project', instruction.projects?.name || '—')}
      ${infoRow('Responded By', [respondedBy, respondedCompany].filter(Boolean).join(', ') || '—')}
      ${infoRow('Response', `<strong style="color:${actionColor};">${responseLabel}</strong>`)}
      ${infoRow('Date', formatDateTime(respondedAt))}
      ${expectedDate ? infoRow('Expected Instruction Date', formatDate(expectedDate)) : ''}
      ${responseNotes ? infoRow('Notes', responseNotes) : ''}
    </table>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      Log in to Proline to view the full record${response === 'will_instruct' ? ' and raise an IR when the formal instruction is received' : ''}.
    </p>
    ${ctaButton('View in Proline', 'https://proline.project-management.uk')}
  `;

  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── MC Raised IR Notification (to Proline) ──────────────────────────────────

export function mcRaisedIrNotification(reference, mcRef, description, projectName, mcName, mcCompany, date, linkedRef) {
  const subject = `New Instruction Raised by MC — ${reference} (${projectName})`;
  const body = `
    <p style="font-size:16px;color:#374151;margin:0 0 20px;">A new instruction has been raised by the Main Contractor and requires your attention.</p>
    <table style="width:100%;border-collapse:collapse;margin:0 0 24px;">
      ${infoRow('Reference', reference)}
      ${mcRef ? infoRow('MC Reference', mcRef) : ''}
      ${infoRow('Project', projectName)}
      ${infoRow('Date of Instruction', date ? formatDate(date) : '—')}
      ${infoRow('Raised By', `${mcName}${mcCompany ? ` (${mcCompany})` : ''}`)}
      ${linkedRef ? infoRow('In Response To', linkedRef) : ''}
    </table>
    ${description ? `
    <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:14px;margin:0 0 24px;">
      <p style="font-size:12px;font-weight:600;color:#6b7280;margin:0 0 6px;text-transform:uppercase;letter-spacing:0.05em;">Description</p>
      <p style="font-size:14px;color:#374151;margin:0;">${description}</p>
    </div>` : ''}
    <p style="font-size:14px;color:#374151;margin:0 0 24px;">Log in to Proline to review and respond.</p>
    ${ctaButton('View in Proline', 'https://proline.project-management.uk')}
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── MC Instruction Confirmation (to MC QS who raised it) ────────────────────

export function mcInstructionConfirmationEmail({ reference, mcRef, description, projectName, date, time, location, linkedRef, recipientName }) {
  const subject = `Instruction Confirmed — ${reference} — ${projectName}`;
  const body = `
    <div style="text-align:center;margin-bottom:24px;">
      <div style="display:inline-block;background:#f0fdf4;border:2px solid #bbf7d0;border-radius:50%;
        width:56px;height:56px;line-height:56px;font-size:26px;color:#16a34a;margin-bottom:12px;">✓</div>
      <h2 style="margin:0;font-size:20px;color:#16a34a;">Instruction Submitted</h2>
    </div>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      Dear ${recipientName || 'Sir/Madam'},
    </p>
    <p style="font-size:14px;color:#374151;margin:0 0 20px;">
      This email confirms that the following instruction has been received and recorded by Proline Internals Ltd.
    </p>
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${infoRow('Reference', `<strong>${reference}</strong>`)}
      ${infoRow('Project', projectName || '—')}
      ${infoRow('Date of Instruction', date ? formatDate(date) : '—')}
      ${time ? infoRow('Time', time.substring(0, 5)) : ''}
      ${location ? infoRow('Location', location) : ''}
      ${mcRef ? infoRow('Your Reference', mcRef) : ''}
      ${linkedRef ? infoRow('In Response To', linkedRef) : ''}
    </table>
    ${description ? `
    <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:6px;padding:14px 18px;margin:0 0 24px;">
      <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px;">Works Instructed</div>
      <p style="margin:0;font-size:13px;color:#374151;line-height:1.6;">${description}</p>
    </div>` : ''}
    ${jctBox(
      `This instruction has been formally received and recorded by Proline Internals Ltd on ${date ? formatDate(date) : 'the date shown above'}. ` +
      `Proline Internals Ltd will proceed in accordance with the subcontract conditions. ` +
      `This email serves as acknowledgement of receipt of your instruction.`
    )}
    <p style="font-size:13px;color:#6b7280;text-align:center;margin:16px 0 0;">
      This instruction has been recorded in the Proline Commercial Management System.
    </p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── MC Registration Notification (to Proline admin) ─────────────────────────

export function mcRegistrationNotification(fullName, email, company, siteNameGiven) {
  const subject = `New MC Portal Access Request — ${fullName} (${company})`;
  const body = `
    <p style="font-size:16px;color:#374151;margin:0 0 20px;">New MC portal access request received.</p>
    <table style="width:100%;border-collapse:collapse;margin:0 0 24px;">
      ${infoRow('Name', fullName)}
      ${infoRow('Email', email)}
      ${infoRow('Company', company)}
      ${infoRow('Site / Project Given', siteNameGiven || '—')}
    </table>
    <p style="font-size:14px;color:#374151;margin:0 0 24px;">
      Log in to the Proline Admin panel to assign this user to a project and approve or reject their request.
    </p>
    ${ctaButton('Open Admin Panel', 'https://proline.project-management.uk')}
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── MC Access Approved ───────────────────────────────────────────────────────

export function mcAccessApprovedEmail(fullName, projectName) {
  const subject = 'Your Proline MC Portal Access Has Been Approved';
  const body = `
    <p style="font-size:16px;color:#374151;margin:0 0 20px;">Hi ${fullName},</p>
    <p style="font-size:14px;color:#374151;margin:0 0 16px;">
      Your request to access the <strong>Proline Commercial Management System</strong> has been approved.
    </p>
    ${projectName ? `<p style="font-size:14px;color:#374151;margin:0 0 16px;">You have been assigned to project: <strong>${projectName}</strong></p>` : ''}
    <p style="font-size:14px;color:#374151;margin:0 0 24px;">
      You can now log in to the MC portal to review dayworks, variations, and instructions for your project.
    </p>
    ${ctaButton('Log In to Proline', 'https://proline.project-management.uk')}
    <p style="font-size:14px;color:#374151;margin:24px 0 0;">
      Kind regards,<br/>
      <strong>Proline Internals Ltd</strong>
    </p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── MC Access Rejected ───────────────────────────────────────────────────────

export function mcAccessRejectedEmail(fullName, reason) {
  const subject = 'Proline MC Portal Access Request — Not Approved';
  const body = `
    <p style="font-size:16px;color:#374151;margin:0 0 20px;">Hi ${fullName},</p>
    <p style="font-size:14px;color:#374151;margin:0 0 16px;">
      Unfortunately your request to access the <strong>Proline Commercial Management System</strong> has not been approved at this time.
    </p>
    ${reason ? `
    <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:16px;margin:0 0 20px;">
      <p style="font-size:13px;font-weight:600;color:#991b1b;margin:0 0 6px;">Reason:</p>
      <p style="font-size:14px;color:#374151;margin:0;">${reason}</p>
    </div>` : ''}
    <p style="font-size:14px;color:#374151;margin:0 0 16px;">
      If you believe this is an error or have any questions, please contact the Proline team directly.
    </p>
    <p style="font-size:14px;color:#374151;margin:0;">
      Kind regards,<br/>
      <strong>Proline Internals Ltd</strong>
    </p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

// ─── Account Approved ────────────────────────────────────────────────────────

export function varRaisedNotification(variation, project, instruction, qsProfile, raisedByProfile, instrAttachments = []) {
  const subject = `[Action Required] Variation Request Raised — ${variation.reference}`;
  const body = `
    <p style="font-size:16px;color:#374151;margin:0 0 20px;">Hi ${qsProfile.full_name},</p>
    <p style="font-size:14px;color:#374151;margin:0 0 16px;">
      A variation request has been raised by <strong>${raisedByProfile.full_name}</strong> on <strong>${project.name}</strong> and requires your pricing and submission to the MC.
    </p>
    <table style="width:100%;border-collapse:collapse;margin:0 0 20px;">
      <tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;width:40%;font-size:13px;color:#374151;">Reference</td><td style="padding:8px 12px;border:1px solid #e2e8f0;font-size:13px;color:#374151;font-family:monospace;font-weight:700;">${variation.reference}</td></tr>
      <tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;font-size:13px;color:#374151;">Project</td><td style="padding:8px 12px;border:1px solid #e2e8f0;font-size:13px;color:#374151;">${project.reference} — ${project.name}</td></tr>
      <tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;font-size:13px;color:#374151;">Instruction</td><td style="padding:8px 12px;border:1px solid #e2e8f0;font-size:13px;color:#374151;font-family:monospace;">${instruction?.reference || '—'}</td></tr>
      <tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;font-size:13px;color:#374151;">Title</td><td style="padding:8px 12px;border:1px solid #e2e8f0;font-size:13px;color:#374151;">${variation.title || '—'}</td></tr>
      <tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;font-size:13px;color:#374151;">Raised By</td><td style="padding:8px 12px;border:1px solid #e2e8f0;font-size:13px;color:#374151;">${raisedByProfile.full_name}</td></tr>
      ${variation.site_notes ? `<tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;font-size:13px;color:#374151;">Notes from Site</td><td style="padding:8px 12px;border:1px solid #e2e8f0;font-size:13px;color:#374151;">${variation.site_notes}</td></tr>` : ''}
    </table>
    ${instrAttachments.length > 0 ? `
    <div style="margin:0 0 20px;">
      <p style="font-size:13px;font-weight:600;color:#374151;margin:0 0 8px;">Instruction Attachments:</p>
      <ul style="margin:0;padding:0 0 0 20px;">
        ${instrAttachments.map(a => `<li style="font-size:13px;color:#374151;margin:0 0 4px;">${a.file_name}</li>`).join('')}
      </ul>
      <p style="font-size:12px;color:#6b7280;margin:8px 0 0;">You can download these from the variation detail page in Proline.</p>
    </div>` : ''}
    <p style="font-size:14px;color:#374151;margin:0 0 24px;">
      Please log in to review the request, add pricing details, and submit the variation quotation to the main contractor.
    </p>
    <div style="text-align:center;margin:0 0 28px;">
      <a href="https://proline.project-management.uk" style="display:inline-block;background:${NAVY};color:#ffffff;padding:14px 32px;border-radius:6px;font-size:15px;font-weight:600;text-decoration:none;letter-spacing:0.02em;">
        Open in Proline
      </a>
    </div>
    <p style="font-size:14px;color:#374151;margin:0;">
      Kind regards,<br/>
      <strong>Proline Internals Ltd</strong>
    </p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}

export function accountApprovedEmail(fullName) {
  const subject = 'Your Proline Access Has Been Approved';
  const body = `
    <p style="font-size:16px;color:#374151;margin:0 0 20px;">Hi ${fullName},</p>
    <p style="font-size:14px;color:#374151;margin:0 0 16px;">
      Your request to access the <strong>Proline Commercial Management System</strong> has been approved.
    </p>
    <p style="font-size:14px;color:#374151;margin:0 0 24px;">
      You can now log in using the link below:
    </p>
    <div style="text-align:center;margin:0 0 28px;">
      <a href="https://proline.project-management.uk" style="display:inline-block;background:#1a1a2e;color:#ffffff;padding:14px 32px;border-radius:6px;font-size:15px;font-weight:600;text-decoration:none;letter-spacing:0.02em;">
        Log In to Proline
      </a>
    </div>
    <p style="font-size:14px;color:#374151;margin:0 0 16px;">
      If you have any questions, please contact the Proline team.
    </p>
    <p style="font-size:14px;color:#374151;margin:0;">
      Kind regards,<br/>
      <strong>Proline Internals Ltd</strong>
    </p>
  `;
  return { subject, htmlBody: baseLayout(subject, body) };
}
